'use client'

import * as React from 'react'
import { type DateRange, DayPicker, getDefaultClassNames } from 'react-day-picker'
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, isSameDay } from 'date-fns'
import { LuChevronLeft, LuChevronRight, LuCalendar } from 'react-icons/lu'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface PresetOption {
  label: string
  value: string
  getRange: () => DateRange
}

const presetOptions: PresetOption[] = [
  {
    label: 'Last 7 days',
    value: '7d',
    getRange: () => ({
      from: subDays(new Date(), 6),
      to: new Date(),
    }),
  },
  {
    label: 'Last 14 days',
    value: '14d',
    getRange: () => ({
      from: subDays(new Date(), 13),
      to: new Date(),
    }),
  },
  {
    label: 'Last 30 days',
    value: '30d',
    getRange: () => ({
      from: subDays(new Date(), 29),
      to: new Date(),
    }),
  },
  {
    label: 'Last 90 days',
    value: '90d',
    getRange: () => ({
      from: subDays(new Date(), 89),
      to: new Date(),
    }),
  },
  {
    label: 'This month',
    value: 'this_month',
    getRange: () => ({
      from: startOfMonth(new Date()),
      to: new Date(),
    }),
  },
  {
    label: 'Last month',
    value: 'last_month',
    getRange: () => ({
      from: startOfMonth(subMonths(new Date(), 1)),
      to: endOfMonth(subMonths(new Date(), 1)),
    }),
  },
  {
    label: 'This year',
    value: 'this_year',
    getRange: () => ({
      from: startOfYear(new Date()),
      to: new Date(),
    }),
  },
  {
    label: 'Last year',
    value: 'last_year',
    getRange: () => ({
      from: startOfYear(subMonths(new Date(), 12)),
      to: endOfYear(subMonths(new Date(), 12)),
    }),
  },
]

interface DateRangePickerProps {
  value?: DateRange
  onChange?: (range: DateRange | undefined) => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
  onApply?: (range: DateRange | undefined) => void
}

const DateRangePicker = ({
  value: externalValue,
  onChange: externalOnChange,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  className,
  onApply,
}: DateRangePickerProps) => {
  // Internal state for uncontrolled mode - default to last 30 days
  const [internalValue, setInternalValue] = React.useState<DateRange | undefined>({
    from: subDays(new Date(), 29),
    to: new Date(),
  })
  const [internalOpen, setInternalOpen] = React.useState(false)
  const [selectedPreset, setSelectedPreset] = React.useState<string | null>('30d')
  const [displayMonth, setDisplayMonth] = React.useState<Date>(subMonths(new Date(), 1))

  // Use external values if provided
  const value = externalValue !== undefined ? externalValue : internalValue
  const onChange = externalOnChange || setInternalValue
  const open = externalOpen !== undefined ? externalOpen : internalOpen
  const onOpenChange = externalOnOpenChange || setInternalOpen

  // Determine which preset is currently selected
  const getActivePreset = React.useCallback((range: DateRange | undefined): string | null => {
    if (!range?.from || !range?.to) return null

    for (const preset of presetOptions) {
      const presetRange = preset.getRange()
      if (
        presetRange.from &&
        presetRange.to &&
        isSameDay(range.from, presetRange.from) &&
        isSameDay(range.to, presetRange.to)
      ) {
        return preset.value
      }
    }
    return null
  }, [])

  // Sync selectedPreset when value changes externally (e.g., from saved views)
  React.useEffect(() => {
    const activePreset = getActivePreset(value)
    setSelectedPreset(activePreset)
  }, [value, getActivePreset])

  // Handle preset selection
  const handlePresetSelect = (preset: PresetOption) => {
    const range = preset.getRange()
    onChange(range)
    setSelectedPreset(preset.value)
    // Update display month to show the range
    if (range.from) {
      setDisplayMonth(subMonths(range.to || range.from, 1))
    }
  }

  // Handle calendar selection
  const handleCalendarSelect = (range: DateRange | undefined) => {
    onChange(range)
    setSelectedPreset(getActivePreset(range))
  }

  // Get display text for the trigger button
  const getDisplayText = () => {
    if (!value?.from) return 'Select date range'
    if (!value.to) return format(value.from, 'MMM d, yyyy')
    return `${format(value.from, 'MMM d')} - ${format(value.to, 'MMM d, yyyy')}`
  }

  // Handle apply button
  const handleApply = () => {
    if (onApply) {
      onApply(value)
    }
    onOpenChange(false)
  }

  // Handle cancel button
  const handleCancel = () => {
    onOpenChange(false)
  }

  const defaultClassNames = getDefaultClassNames()

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="transparent-outline"
          size="xs"
          className={cn(
            'hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]',
            'focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2',
            open && 'bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0',
            className
          )}
        >
          <LuCalendar className="size-4" />
          {getDisplayText()}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 border-[#EDEEF3] shadow-lg"
        align="start"
        sideOffset={6}
      >
        <div className="flex">
          {/* Presets Sidebar */}
          <div className="w-[140px] border-r border-[#EDEEF3] py-2">
            <div className="px-3 py-1.5 mb-1">
              <span className="text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
                Quick Select
              </span>
            </div>
            <div className="space-y-0.5">
              {presetOptions.map((preset) => {
                const isActive = selectedPreset === preset.value
                return (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => handlePresetSelect(preset)}
                    className={cn(
                      'w-full px-3 py-1.5 text-left text-[13px] transition-colors cursor-pointer',
                      isActive
                        ? 'bg-[#F2EEFF] text-[#654EDA] font-medium'
                        : 'text-[#08090A] hover:bg-[#F7F8F8]'
                    )}
                  >
                    {preset.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Calendar Section */}
          <div className="flex flex-col">
            <DayPicker
              mode="range"
              selected={value}
              onSelect={handleCalendarSelect}
              numberOfMonths={2}
              month={displayMonth}
              onMonthChange={setDisplayMonth}
              showOutsideDays={true}
              className={cn(
                'p-4 bg-white',
                String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
                String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
              )}
              classNames={{
                root: cn('w-fit', defaultClassNames.root),
                months: cn(
                  'flex gap-6 flex-row relative',
                  defaultClassNames.months
                ),
                month: cn('flex flex-col w-full gap-4', defaultClassNames.month),
                nav: cn(
                  'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between',
                  defaultClassNames.nav
                ),
                button_previous: cn(
                  buttonVariants({ variant: 'ghost' }),
                  'size-8 p-0 select-none hover:bg-[#F7F8F8] hover:text-[#654EDA]',
                  defaultClassNames.button_previous
                ),
                button_next: cn(
                  buttonVariants({ variant: 'ghost' }),
                  'size-8 p-0 select-none hover:bg-[#F7F8F8] hover:text-[#654EDA]',
                  defaultClassNames.button_next
                ),
                month_caption: cn(
                  'flex items-center justify-center h-8 w-full px-8',
                  defaultClassNames.month_caption
                ),
                caption_label: cn(
                  'select-none font-medium text-[14px] text-[#08090A]',
                  defaultClassNames.caption_label
                ),
                table: 'w-full border-collapse',
                weekdays: cn('flex', defaultClassNames.weekdays),
                weekday: cn(
                  'text-[#8A8F98] rounded-md w-9 font-medium text-[12px] select-none',
                  defaultClassNames.weekday
                ),
                week: cn('flex w-full mt-1', defaultClassNames.week),
                day: cn(
                  'relative p-0 text-center group/day select-none',
                  '[&:last-child[data-selected=true]_button]:rounded-r-md',
                  '[&:first-child[data-selected=true]_button]:rounded-l-md',
                  defaultClassNames.day
                ),
                range_start: cn(
                  'rounded-l-md bg-[#F2EEFF]',
                  defaultClassNames.range_start
                ),
                range_middle: cn(
                  'rounded-none bg-[#F2EEFF]',
                  defaultClassNames.range_middle
                ),
                range_end: cn(
                  'rounded-r-md bg-[#F2EEFF]',
                  defaultClassNames.range_end
                ),
                today: cn(
                  'bg-[#F7F8F8] text-[#08090A] rounded-md data-[selected=true]:rounded-none',
                  defaultClassNames.today
                ),
                outside: cn(
                  'invisible pointer-events-none',
                  defaultClassNames.outside
                ),
                disabled: cn(
                  'text-[#D1D5DB] opacity-50',
                  defaultClassNames.disabled
                ),
                hidden: cn('invisible', defaultClassNames.hidden),
              }}
              components={{
                Root: ({ className, rootRef, ...props }) => (
                  <div
                    data-slot="calendar"
                    ref={rootRef}
                    className={cn(className)}
                    {...props}
                  />
                ),
                Chevron: ({ className, orientation, ...props }) => {
                  if (orientation === 'left') {
                    return (
                      <LuChevronLeft
                        className={cn('size-4 text-[#08090A]', className)}
                        {...props}
                      />
                    )
                  }
                  return (
                    <LuChevronRight
                      className={cn('size-4 text-[#08090A]', className)}
                      {...props}
                    />
                  )
                },
                DayButton: ({ className, day, modifiers, ...props }) => {
                  const isRangeStart = modifiers.range_start
                  const isRangeEnd = modifiers.range_end
                  const isRangeMiddle = modifiers.range_middle
                  const isSelected = modifiers.selected && !isRangeMiddle
                  const isOutside = modifiers.outside

                  return (
                    <button
                      type="button"
                      data-day={day.date.toLocaleDateString()}
                      data-selected-single={
                        modifiers.selected &&
                        !isRangeStart &&
                        !isRangeEnd &&
                        !isRangeMiddle
                      }
                      data-range-start={isRangeStart}
                      data-range-end={isRangeEnd}
                      data-range-middle={isRangeMiddle}
                      className={cn(
                        'flex items-center justify-center w-9 h-9 text-[13px] font-normal cursor-pointer',
                        'transition-colors rounded-md',
                        'hover:bg-[#F2EEFF] hover:text-[#654EDA]',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#654EDA]/20',
                        modifiers.today && !modifiers.selected && 'bg-[#F7F8F8] font-medium',
                        isOutside && 'invisible pointer-events-none',
                        isRangeMiddle && !isOutside && 'rounded-none bg-[#F2EEFF] text-[#654EDA]',
                        isRangeStart && !isOutside && 'bg-[#654EDA] text-white hover:bg-[#654EDA] hover:text-white rounded-l-md rounded-r-none',
                        isRangeEnd && !isOutside && 'bg-[#654EDA] text-white hover:bg-[#654EDA] hover:text-white rounded-r-md rounded-l-none',
                        isRangeStart && isRangeEnd && !isOutside && 'rounded-md',
                        isSelected && !isRangeStart && !isRangeEnd && !isOutside && 'bg-[#654EDA] text-white hover:bg-[#654EDA]',
                        defaultClassNames.day,
                        className
                      )}
                      {...props}
                    />
                  )
                },
              }}
            />

            {/* Footer with Selected Range and Actions */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#EDEEF3] bg-[#FAFAFA]">
              <div className="flex items-center gap-2">
                <LuCalendar className="size-4 text-[#8A8F98]" />
                <span className="text-[13px] text-[#08090A]">
                  {value?.from && value?.to
                    ? `${format(value.from, 'MMM d, yyyy')} – ${format(value.to, 'MMM d, yyyy')}`
                    : value?.from
                      ? format(value.from, 'MMM d, yyyy')
                      : 'Select a range'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  className="h-[30px] px-3 text-[13px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#F7F8F8]"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleApply}
                  className="h-[30px] px-4 text-[13px] bg-[#654EDA] hover:bg-[#5540C7] text-white"
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { DateRangePicker }
export type { DateRangePickerProps, DateRange }

